<template>
    <div v-if="isValid">
        <div class="print-header show-only-on-print">
            <crosstab-master-title
                ref="title"
                :reportModel="reportModel"
            ></crosstab-master-title>
        </div>
        <h3 class="visually-hidden">BRFSS Logistic Regression</h3>
        <app-navigator
            ref="appNavigator"
            :router="$router"
            :forReport="true"
        ></app-navigator>

        <section-header
            v-if="!isLoading"
            ref="select"
            isFor="header"
            :forReport="true"
            :exportXlsx="exportXlsx"
        ></section-header>
        <div v-if="!isLoading">
            <no-data-table
                :missingData="reportModel.missingData"
            ></no-data-table>
            <div class="hide-on-print">
                <crosstab-master-title
                    ref="title"
                    :reportModel="reportModel"
                ></crosstab-master-title>
            </div>
            <div class="report-table page-break">
                <single-crosstab
                    ref="theCrosstab"
                    :isTotal="true"
                    :control1="reportModel.totalControl1"
                    :control2="reportModel.totalControl2"
                    :reportModel="reportModel"
                ></single-crosstab>
            </div>
            <div class="row hide-on-print report-filter info-container">
                <report-filter
                    v-if="reportModel.levelsGroupedByControl1"
                    :reportModel="reportModel"
                    v-on:filtersChanged="reportFiltersChanged"
                ></report-filter>
            </div>
            <!-- <template v-if="reportModel.levelsGroupedByControl1"> -->
            <template
                v-for="(
                    control1, control1Index
                ) in reportModel.levelsGroupedByControl1"
            >
                <div
                    v-for="(
                        control2, control2Index
                    ) in control1.levelsGroupedByControl2"
                    class="report-table page-break"
                    v-bind:key="control2.key"
                >
                    <single-crosstab
                        ref="theCrosstabs"
                        v-if="control1Index > 0 || control2Index > 0"
                        :control1="control1"
                        :control2="control2"
                        :reportModel="reportModel"
                        :selectedLevels="selectedLevels"
                    ></single-crosstab>
                </div>
            </template>
        </div>
        <div v-if="isLoading">
            <h4>Please wait while report is loading...</h4>
            <div>
                <i class="nccd-spinner-medium margin-auto"></i>
            </div>
        </div>
        <show-errors
            :errorCode="errorCode"
            :errorMessages="errorMessages"
            selectionPath="/crossTabulation/"
            selectionTitle="Cross Tabulation"
        ></show-errors>
    </div>
</template>

<script>
/* eslint no-unused-vars: 'off', one-var: 'off', operator-linebreak: 'off', comma-style: 'off' */
import xlsxBuilder from '@/scripts/crossTabulation/xlsxBuilder';
import apiHelper from '@/scripts/crossTabulation/apiHelper';
import common from '@/scripts/common';
import singleCrosstab from '@/components/crossTabulation/report/singleCrosstab';
import crosstabMasterTitle from '@/components/crossTabulation/report/crosstabMasterTitle';
import reportFilter from '@/components/crossTabulation/report/reportFilter';
import current from '@/scripts/current';
import showErrors from '@/components/showErrors';
import XLSX from 'xlsx';
import commonModelHelper from '@/scripts/commonModelHelper';
import { mapGetters, mapState } from 'vuex';
import metricsHelper from '@/scripts/metricsHelper';
import noDataTable from '@/components/noDataTable';
import store from '@/store';
import _ from 'lodash';
import ctResizer from '@/scripts/crossTabulation/ctResizer';
import appNavigator from '@/components/layout/appNavigator';
import sectionHeader from '@/components/sub/sectionHeader';

export default {
    name: 'viewReport',
    computed: {
        ...mapState({
            hasData: (state) => state.results.hasData,
            allLoaded: (state) => state.loadedControls.allLoaded,
            isLoading: (state) => state.display.isLoading
        })
    },
    components: {
        reportFilter,
        singleCrosstab,
        showErrors,
        crosstabMasterTitle,
        noDataTable,
        appNavigator,
        sectionHeader
    },
    watch: {
        allLoaded(value) {
            if (!value) return;
            ctResizer.resize();
        }
    },
    mounted: function () {
        const vm = this;
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        // Unregister the event listener before destroying this Vue instance
        window.removeEventListener('resize', this.onResize);
    },
    created: function () {
        const vm = this;
        vm.resetClicked = (e) => current.resetClicked(e);
    },
    data: function () {
        var vm = this;

        // process.env.NODE_ENV === 'development' && vm.$route.query.test && vm.$route.query.test.toLowerCase() === 'true';
        current.initializeCrossTabulationValues(vm);
        if (!commonModelHelper.initializeStep(vm, 'viewreport'))
            return { isValid: false };
        var selectionModel = current.selectionModel;
        var apiFormData = apiHelper.getReportParameters(selectionModel);
        var resultsAlreadyLoaded =
            store.state.results.hasData &&
            store.state.results.analysisType === 'ct' &&
            store.state.results.apiFormData === apiFormData;

        metricsHelper.reportRequested();
        store.dispatch('report/setCountsByStateYear');
        var vmData = {
            isValid: true,
            selectionModel: current.selectionModel,
            modelHelper: current.modelHelper,
            common: common,
            errorCode: '',
            errorMessages: [],
            reportModel: {},
            reportModelNoWeighting: null,
            selectedLevels: {
                control1: [],
                control2: []
            },
            testRow: null,
            testColumn: null,
            xlsxWorkspace: {},
            showUnweighted: Boolean(
                store.state.selModel.years.selectionType === 'combine'
            ),
            resizeBounce: null
        };
        if (resultsAlreadyLoaded) {
            apiHelper.populateReportModel(store.state.results.data, vmData);
            metricsHelper.reportLoadCached();
        } else
            apiHelper.loadFromApi(
                vmData,
                apiFormData,
                vmData.showUnweighted,
                vm.$router
            );

        vmData.resetClicked = null;
        return vmData;
    },
    methods: {
        onResize(event) {
            const vm = this;
            if (!vm.resizeBounce)
                vm.resizeBounce = _.debounce(ctResizer.resize, 700);
            vm.resizeBounce();
        },
        reportFiltersChanged,
        getGlyphicon: function (statisticDefinition) {
            switch (statisticDefinition.apiName) {
                case 'sampleSize':
                    return 'glyphicon glyphicon-thumbs-up';
                case 'rowPercentage':
                    return 'glyphicon glyphicon-resize-horizontal';
                case 'columnPercentage':
                    return 'glyphicon glyphicon-resize-vertical';
                case 'totalPercentage':
                    return 'glyphicon glyphicon-fullscreen';
                case 'weightedN':
                    return 'glyphicon glyphicon-scale';
                default:
                    return statisticDefinition.apiName;
            }
        },
        getTableWidth: function (control2) {
            var vm = this;
            var totalStatistics =
                control2.levelsGroupedByRows[
                    control2.levelsGroupedByRows.length - 1
                ].totalStatistics;
            var largestNumber =
                totalStatistics.weightedN !== undefined &&
                totalStatistics.weightedN !== null
                    ? totalStatistics.weightedN.value
                    : totalStatistics.sampleSize;
            var digits = largestNumber.toString().length;
            var numberOfStatisticColumns = control2.levelsGroupedByRows.length;
            var firstColumnWidth = 140;
            var hasCi =
                current.selectionModel.selectedStatistics.confidenceInterval;
            var hasSe = current.selectionModel.selectedStatistics.standardError;
            var statWidth = 40;
            var ciWidth = 120;
            var seWidth = 40;
            var widthForDigit = 20;
            var columnWidth =
                statWidth +
                (statWidth +
                    (hasCi ? ciWidth : 0) +
                    (hasSe ? seWidth : 0) +
                    digits * widthForDigit);
            return firstColumnWidth + numberOfStatisticColumns * columnWidth;
        },
        exportXlsx,
        downloadJson
    }
};

function getTheCrosstabs(theRef) {
    if (!theRef) return [];
    if (theRef instanceof Array) return theRef;
    if (theRef instanceof Object) return [theRef];
    return theRef;
}

function reportFiltersChanged(selectedLevels) {
    const vm = this;
    vm.selectedLevels = selectedLevels;
    let theCrosstabs = getTheCrosstabs(vm.$refs.theCrosstabs);
    theCrosstabs.forEach((c) => c.reportFiltersChanged(selectedLevels));
}

function downloadJson() {
    var vm = this;
    const data = JSON.stringify(vm.reportModel.originalData);
    const blob = new Blob([data], { type: 'text/plain' });
    const e = document.createEvent('MouseEvents'),
        a = document.createElement('a');
    //a.download = vm.apiQueryString + '.json';
    a.download = apiHelper.getJsonFileName(current.selectionModel);
    a.href = window.URL.createObjectURL(blob);
    a.dataset.downloadurl = ['text/json', a.download, a.href].join(':');
    e.initEvent(
        'click',
        true,
        false,
        window,
        0,
        0,
        0,
        0,
        0,
        false,
        false,
        false,
        false,
        0,
        null
    );
    a.dispatchEvent(e);
}

function exportXlsx(e) {
    const vm = this;
    if (e) e.preventDefault();
    metricsHelper.reportDownloaded();
    let wb = xlsxBuilder.createWorkbook(vm.reportModel, store.state, vm);
    if (wb) XLSX.writeFile(wb, 'sheetjs.xlsx');
}
</script>

<style scoped="">
/*.hyperlink-button {
    background-color: transparent;
    border: none;
    text-decoration: underline;
    color: #075290;
    padding-left: 0px;
  }

  .hyperlink-button:hover {
    text-decoration: none;
  }*/

.print-header {
    /*width:250px;*/
    display: none;
}

@media print {
    .print-header {
        /*width:250px;*/
        display: inline-block;
        font-size: xx-large;
        margin: 0 0 40px 0px;
        width: 100%;
        text-align: center;
    }
}

.report-filter {
    margin-top: 80px;
}

.download-reset-buttons :nth-child(2) {
    float: right;
}

.hidden {
    visibility: hidden;
    display: none !important;
}
</style>
